<template>

    <Head
        :title="isEditMode ? 'Редактирование профиля' : 'Заполнение профиля'" />

    <MainLayout>
        <div class="profile-page">
            <PageHeader :title="pageTitle">
                <p class="page-subtitle" v-if="isEditMode">Внесите изменения в данные профиля.</p>
                <p class="page-subtitle" v-else>Заполните данные профиля для получения доступа к личной статистике</p>
            </PageHeader>

            <div v-if="page.props.flash?.error" class="alert alert-warning">
                <strong>Доступ ограничен</strong>
                <p>{{ page.props.flash.error }}</p>
            </div>

            <div v-if="isProfileCompleted" class="alert alert-success">
                <strong>Профиль заполнен</strong>
                <p>Ваш профиль сохранён. Теперь у вас есть доступ к личной статистике.</p>
            </div>

            <form @submit.prevent="submit" class="profile-form">
                <!-- Фото профиля и личные данные -->
                <section class="form-section profile-photo-section">
                    <div class="profile-photo-layout">
                        <!-- Фото слева (карусель по сезонам) -->
                        <div class="profile-photo-container">
                            <h2>Фото профиля</h2>
                            <PhotoCarousel :photos="carouselPhotos" :fallback-url="profilePhotoUrl"
                                :initials="getInitials()" />
                            <p class="photo-hint">Загрузите фото в строке сезона ниже</p>
                        </div>

                        <!-- Данные справа -->
                        <div class="profile-data-container">
                            <h2>Личные данные</h2>

                            <!-- Фамилия -->
                            <div class="form-row">
                                <div class="form-group">
                                    <label class="input-label" for="surname">
                                        Фамилия<span v-if="isBlank(form.surname)" class="required-mark" aria-hidden="true">*</span>
                                    </label>
                                    <UiInput id="surname" v-model="form.surname" label="" block required
                                        :error="form.errors.surname" />
                                </div>

                                <div class="form-group">
                                    <label class="input-label" for="surname_en">
                                        Фамилия (англ.)<span v-if="isBlank(form.surname_en)" class="required-mark" aria-hidden="true">*</span>
                                    </label>
                                    <UiInput id="surname_en" v-model="form.surname_en" label="" block required
                                        :error="form.errors.surname_en" />
                                </div>
                            </div>

                            <!-- Имя -->
                            <div class="form-row">
                                <div class="form-group">
                                    <label class="input-label" for="first_name">
                                        Имя<span v-if="isBlank(form.first_name)" class="required-mark" aria-hidden="true">*</span>
                                    </label>
                                    <UiInput id="first_name" v-model="form.first_name" label="" block required
                                        autocomplete="given-name"
                                        :error="form.errors.first_name" />
                                </div>

                                <div class="form-group">
                                    <label class="input-label" for="first_name_en">
                                        Имя (англ.)<span v-if="isBlank(form.first_name_en)" class="required-mark" aria-hidden="true">*</span>
                                    </label>
                                    <UiInput id="first_name_en" v-model="form.first_name_en" label="" block required
                                        :error="form.errors.first_name_en" />
                                </div>
                            </div>

                            <!-- Отчество и Гражданство -->
                            <div class="form-row">
                                <div class="form-group">
                                    <label class="input-label" for="middle_name">Отчество</label>
                                    <div class="input-with-checkbox">
                                        <UiInput id="middle_name" v-model="form.middle_name" label="" block
                                            :error="form.errors.middle_name" />
                                        <label class="checkbox-label">
                                            <input type="checkbox" class="checkbox ui-checkbox"
                                                v-model="form.middle_name_visible" />
                                            <span>Видно всем</span>
                                        </label>
                                    </div>
                                    <div v-if="form.errors.middle_name" class="input-error">
                                        <p>{{ form.errors.middle_name }}</p>
                                    </div>
                                </div>

                                <div class="form-group">
                                    <label class="input-label" for="nationality">
                                        Гражданство<span v-if="isBlank(form.nationality)" class="required-mark" aria-hidden="true">*</span>
                                    </label>
                                    <UiSelect id="nationality" v-model="form.nationality" label="" block
                                        :options="nationalityOptions" value-type="string" placeholder="Выберите гражданство"
                                        :error="form.errors.nationality" />
                                </div>
                            </div>

                            <!-- Дата рождения и спортивный разряд -->
                            <div class="form-row">
                                <div class="form-group">
                                    <label class="input-label" for="birth_date">
                                        Дата рождения<span v-if="isBlank(form.birth_date)" class="required-mark" aria-hidden="true">*</span>
                                    </label>
                                    <UiInput id="birth_date" v-model="form.birth_date" type="date" label="" block required
                                        :error="form.errors.birth_date" />
                                </div>

                                <div class="form-group">
                                    <label class="input-label" for="rank">
                                        Спортивный разряд<span v-if="isBlank(form.rank)" class="required-mark" aria-hidden="true">*</span>
                                    </label>
                                    <UiSelect id="rank" v-model="form.rank" label="" block required :options="rankOptions"
                                        value-type="string" placeholder="Не указан"
                                        :error="form.errors.rank" />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Контакты -->
                <section class="form-section">
                    <h2>Контакты</h2>

                    <div class="form-grid">
                        <div class="form-group">
                            <div class="input-with-checkbox">
                                <UiInput id="instagram" v-model="form.instagram" label="" block placeholder="@username"
                                    :error="form.errors.instagram">
                                    <template #prefix>
                                        <img src="/images/instagram.png" alt="Instagram" class="ui-control__prefix-icon" />
                                    </template>
                                </UiInput>
                                <label class="checkbox-label">
                                    <input type="checkbox" class="checkbox ui-checkbox" v-model="form.instagram_visible" />
                                    <span>Видно всем</span>
                                </label>
                            </div>
                            <div v-if="form.errors.instagram" class="input-error">
                                <p>{{ form.errors.instagram }}</p>
                            </div>
                        </div>

                        <div class="form-group">
                            <div class="input-with-checkbox">
                                <UiInput id="telegram" v-model="form.telegram" label="" block placeholder="@username"
                                    :error="form.errors.telegram">
                                    <template #prefix>
                                        <img src="/images/telegram.png" alt="Telegram" class="ui-control__prefix-icon" />
                                    </template>
                                </UiInput>
                                <label class="checkbox-label">
                                    <input type="checkbox" class="checkbox ui-checkbox" v-model="form.telegram_visible" />
                                    <span>Видно всем</span>
                                </label>
                            </div>
                            <div v-if="form.errors.telegram" class="input-error">
                                <p>{{ form.errors.telegram }}</p>
                            </div>
                        </div>

                        <div class="form-group">
                            <div class="input-with-checkbox">
                                <UiInput id="email" v-model="form.email" type="email" label="" block placeholder="Email"
                                    autocomplete="email"
                                    :error="form.errors.email">
                                    <template #prefix>
                                        <img src="/images/email.png" alt="Email" class="ui-control__prefix-icon" />
                                    </template>
                                </UiInput>
                                <label class="checkbox-label">
                                    <input type="checkbox" class="checkbox ui-checkbox" v-model="form.email_visible" />
                                    <span>Видно всем</span>
                                </label>
                            </div>
                            <div v-if="form.errors.email" class="input-error">
                                <p>{{ form.errors.email }}</p>
                            </div>
                        </div>

                        <div class="form-group">
                            <div class="input-with-checkbox">
                                <UiInput id="phone" v-model="form.phone" type="tel" label="" block placeholder="Телефон"
                                    autocomplete="tel"
                                    :error="form.errors.phone">
                                    <template #prefix>
                                        <img src="/images/phone.png" alt="Телефон" class="ui-control__prefix-icon" />
                                    </template>
                                </UiInput>
                                <label class="checkbox-label">
                                    <input type="checkbox" class="checkbox ui-checkbox" v-model="form.phone_visible" />
                                    <span>Видно всем</span>
                                </label>
                            </div>
                            <div v-if="form.errors.phone" class="input-error">
                                <p>{{ form.errors.phone }}</p>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Физические данные -->
                <section class="form-section">
                    <h2>Физические данные</h2>

                    <div class="form-grid">
                        <div class="form-group">
                            <label class="input-label" for="height">
                                Рост (см)<span v-if="isBlank(form.height)" class="required-mark" aria-hidden="true">*</span>
                            </label>
                            <UiInput id="height" v-model="form.height" type="number" label="" block min="140" max="230"
                                required @blur="validateHeight"
                                :error="form.errors.height" />
                        </div>

                        <div class="form-group">
                            <label class="input-label" for="weight">Вес (кг)</label>
                            <div class="input-with-checkbox">
                                <UiInput id="weight" v-model="form.weight" type="number" label="" block min="40" max="150"
                                    @blur="validateWeight" :error="form.errors.weight" />
                                <label class="checkbox-label">
                                    <input type="checkbox" class="checkbox ui-checkbox" v-model="form.weight_visible" />
                                    <span>Видно всем</span>
                                </label>
                            </div>
                            <div v-if="form.errors.weight" class="input-error">
                                <p>{{ form.errors.weight }}</p>
                            </div>
                        </div>

                        <div class="form-group">
                            <label class="input-label" for="jump_height">Высота прыжка (см)</label>
                            <div class="input-with-checkbox">
                                <UiInput id="jump_height" v-model="form.jump_height" type="number" label="" block min="250"
                                    max="450" @blur="validateJumpHeight"
                                    :error="form.errors.jump_height" />
                                <label class="checkbox-label">
                                    <input type="checkbox" class="checkbox ui-checkbox" v-model="form.jump_height_visible" />
                                    <span>Видно всем</span>
                                </label>
                            </div>
                            <div v-if="form.errors.jump_height" class="input-error">
                                <p>{{ form.errors.jump_height }}</p>
                            </div>
                        </div>

                        <div class="form-group">
                            <label class="input-label" for="handedness">Рука</label>
                            <div class="input-with-checkbox">
                                <UiSelect id="handedness" v-model="form.handedness" label="" :options="handednessOptions"
                                    value-type="string" placeholder="Выберите"
                                    :error="form.errors.handedness" />
                                <label class="checkbox-label">
                                    <input type="checkbox" class="checkbox ui-checkbox" v-model="form.handedness_visible" />
                                    <span>Видно всем</span>
                                </label>
                            </div>
                            <div v-if="form.errors.handedness" class="input-error">
                                <p>{{ form.errors.handedness }}</p>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Сезоны -->
                <ProfileSeasonsSection v-model:first-season-id="form.first_season_id"
                    v-model:last-season-id="form.last_season_id" v-model:seasons="form.seasons"
                    :seasons-list="seasonsList" :teams="teams" :teams-by-season="teamsBySeason" :positions="positions"
                    :existing-seasons="existingSeasons" :errors="form.errors"
                    @photo-change="onSeasonPhotoChange"
                    @photo-clear="onSeasonPhotoClear" />

                <div class="form-actions">
                    <div v-if="page.props.flash?.success" class="alert alert-success">
                        <strong>Сохранено</strong>
                        <p>{{ page.props.flash.success }}</p>
                    </div>
                    <button type="submit" class="btn btn-primary" :disabled="form.processing || !isFormValid">
                        {{ form.processing ? 'Сохранение...' : isEditMode ? 'Сохранить изменения' : 'Сохранить профиль' }}
                    </button>
                </div>
            </form>
        </div>
    </MainLayout>
</template>

<script setup>
import { Head, useForm, usePage, router } from '@inertiajs/vue3';
import MainLayout from '@/components/layout/MainLayout.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import ProfileSeasonsSection from '@/components/player/ProfileSeasonsSection.vue';
import PhotoCarousel from '@/components/player/PhotoCarousel.vue';
import { ref, computed, onMounted } from 'vue';
import { isBlank } from '@/utils/formHelpers';
import UiInput from '@/components/ui/forms/UiInput.vue';
import UiSelect from '@/components/ui/forms/UiSelect.vue';

const page = usePage();
const nationalityOptions = computed(() => page.props.nationalityOptions ?? []);

const handednessOptions = [
    { value: 'right', label: 'Правша' },
    { value: 'left', label: 'Левша' },
    { value: 'ambidextrous', label: 'Амбидекстр' },
];

const props = defineProps({
    profile: Object,
    profilePhotoUrl: String,
    username: String,
    existingSeasons: Array,
    teams: Array,
    teamsBySeason: Object,
    seasonsList: Array,
    positions: Array,
    isProfileCompleted: Boolean,
    isEditMode: Boolean,
    userStatus: String,
    rankOptions: Array,
});

const pageTitle = computed(() => {
    if (props.isEditMode) {
        return 'Редактирование профиля';
    }
    return 'Заполнение профиля';
});

// Функция для инициализации первого и последнего сезона из existingSeasons
const initializeSeasonsData = () => {
    if (!props.existingSeasons || props.existingSeasons.length === 0) {
        return {
            first_season_id: null,
            last_season_id: null,
        };
    }

    const seasonIds = props.existingSeasons.map(es => es.season_id).sort((a, b) => a - b);

    return {
        first_season_id: seasonIds[0] || null,
        last_season_id: seasonIds[seasonIds.length - 1] || null,
    };
};

const initialSeasonsData = initializeSeasonsData();

const form = useForm({
    // Профиль
    first_name: props.profile?.first_name || '',
    first_name_en: props.profile?.first_name_en || '',
    surname: props.profile?.surname || '',
    surname_en: props.profile?.surname_en || '',
    middle_name: props.profile?.middle_name || '',
    nationality: props.profile?.nationality || '',
    birth_date: props.profile?.birth_date || '',
    rank: props.profile?.rank || '',
    instagram: props.profile?.instagram || '',
    telegram: props.profile?.telegram || '',
    email: props.profile?.email || '',
    phone: props.profile?.phone || '',
    height: props.profile?.height ?? null,
    weight: props.profile?.weight ?? null,
    jump_height: props.profile?.jump_height ?? null,
    handedness: props.profile?.handedness || 'right',
    // Видимость (first_name, surname, birth_date, height, nationality всегда видимы)
    middle_name_visible: Boolean(props.profile?.middle_name_visible ?? true),
    instagram_visible: Boolean(props.profile?.instagram_visible ?? true),
    telegram_visible: Boolean(props.profile?.telegram_visible ?? true),
    email_visible: Boolean(props.profile?.email_visible ?? true),
    phone_visible: Boolean(props.profile?.phone_visible ?? true),
    weight_visible: Boolean(props.profile?.weight_visible ?? true),
    jump_height_visible: Boolean(props.profile?.jump_height_visible ?? true),
    handedness_visible: Boolean(props.profile?.handedness_visible ?? true),
    // Сезоны - инициализируем первый и последний сезон на основе existingSeasons
    // seasons будет заполнен автоматически в ProfileSeasonsSection при монтировании
    first_season_id: initialSeasonsData.first_season_id,
    last_season_id: initialSeasonsData.last_season_id,
    seasons: [],
});

// === Фото по сезонам ===
const seasonPhotoFiles = ref({});
const seasonPhotoPreviewUrls = ref({});
const seasonPhotoRemovals = ref({});

const getSeasonPhotoUrl = (entry) =>
    seasonPhotoPreviewUrls.value[entry?.client_key] || entry?.photo_url || null;

const resolveTeamName = (seasonId, teamId) => {
    if (!teamId) {
        return null;
    }

    const seasonTeams = props.teamsBySeason?.[seasonId] ?? props.teamsBySeason?.[String(seasonId)] ?? [];
    const seasonTeam = seasonTeams.find(team => team.id === teamId);
    if (seasonTeam?.name) {
        return seasonTeam.name;
    }

    return props.teams?.find(team => team.id === teamId)?.name ?? null;
};

const resolvePositionName = (positionId) =>
    props.positions?.find(position => position.id === positionId)?.name ?? null;

const buildCarouselCaption = (seasonName, teamName, positionName) =>
    [seasonName, teamName, positionName].filter(Boolean).join(' · ');

const carouselPhotos = computed(() => {
    const photos = [];

    for (const group of form.seasons || []) {
        const seasonId = group.season_id;
        const season = props.seasonsList?.find(s => s.id === seasonId);
        const seasonName = season ? season.name : `Сезон ${seasonId}`;
        const shortMatch = seasonName.match(/(\d{2,4}[-–]\d{2,4})/);
        const seasonShort = shortMatch ? shortMatch[1] : String(seasonId);

        for (const entry of group.entries || []) {
            if (entry.status !== 'played') {
                continue;
            }

            const url = getSeasonPhotoUrl(entry);
            if (!url) {
                continue;
            }

            const teamName = resolveTeamName(seasonId, entry.team_id);
            const positionName = resolvePositionName(entry.position_id);

            photos.push({
                seasonId,
                seasonName,
                seasonShort,
                url,
                caption: buildCarouselCaption(seasonShort, teamName, positionName),
            });
        }
    }

    return photos.sort((a, b) => b.seasonId - a.seasonId);
});

const onSeasonPhotoChange = ({ clientKey, file, previewUrl }) => {
    if (!clientKey) {
        return;
    }

    if (seasonPhotoRemovals.value[clientKey]) {
        const { [clientKey]: _, ...restRemovals } = seasonPhotoRemovals.value;
        seasonPhotoRemovals.value = restRemovals;
    }

    seasonPhotoFiles.value = {
        ...seasonPhotoFiles.value,
        [clientKey]: file,
    };
    seasonPhotoPreviewUrls.value = {
        ...seasonPhotoPreviewUrls.value,
        [clientKey]: previewUrl,
    };
};

const onSeasonPhotoClear = ({ clientKey, hadServerPhoto = false }) => {
    if (!clientKey) {
        return;
    }

    if (seasonPhotoFiles.value[clientKey]) {
        const { [clientKey]: _, ...restFiles } = seasonPhotoFiles.value;
        seasonPhotoFiles.value = restFiles;
    }

    if (seasonPhotoPreviewUrls.value[clientKey]) {
        const { [clientKey]: _, ...restPreviews } = seasonPhotoPreviewUrls.value;
        seasonPhotoPreviewUrls.value = restPreviews;
    }

    if (hadServerPhoto) {
        seasonPhotoRemovals.value = {
            ...seasonPhotoRemovals.value,
            [clientKey]: true,
        };
    }
};

const getPositionById = (positionId) => {
    if (!positionId) return null;
    return props.positions.find(p => p.id === positionId);
};

const roundValue = (value) => {
    const num = Number(value);
    if (isNaN(num)) return 0;
    return Math.round(num);
};


const getInitials = () => {
    const firstName = props.profile?.first_name || '';
    const surname = props.profile?.surname || '';
    return (firstName.charAt(0) + surname.charAt(0)).toUpperCase() || 'Т';
};

// Валидация физических данных при потере фокуса
const validateHeight = () => {
    const value = Number(form.height);
    if (form.height !== null && form.height !== '' && !isNaN(value)) {
        if (value < 140) form.height = 140;
        else if (value > 230) form.height = 230;
    }
};

const validateWeight = () => {
    const value = Number(form.weight);
    if (form.weight !== null && form.weight !== '' && !isNaN(value)) {
        if (value < 40) form.weight = 40;
        else if (value > 150) form.weight = 150;
    }
};

const validateJumpHeight = () => {
    const value = Number(form.jump_height);
    if (form.jump_height !== null && form.jump_height !== '' && !isNaN(value)) {
        if (value < 250) form.jump_height = 250;
        else if (value > 450) form.jump_height = 450;
    }
};

// Вычисляемое свойство для проверки заполненности обязательных полей
const validateSeasonEntries = (seasons) => {
    if (!seasons?.length) return false;

    return seasons.every((season) => season.entries.every((entry) => {
        if (entry.status !== 'played') return true;
        return Boolean(entry.team_id && entry.position_id);
    }));
};

const isFormValid = computed(() => {
    return form.surname &&
        form.first_name &&
        form.surname_en &&
        form.first_name_en &&
        form.nationality &&
        form.rank &&
        form.birth_date &&
        form.height &&
        form.first_season_id &&
        form.last_season_id &&
        form.seasons.length > 0 &&
        validateSeasonEntries(form.seasons);
});

const visibilityFields = [
    'middle_name_visible',
    'instagram_visible',
    'telegram_visible',
    'email_visible',
    'phone_visible',
    'weight_visible',
    'jump_height_visible',
    'handedness_visible',
];

const appendToFormData = (formData, key, value) => {
    if (value === null || value === undefined) {
        return;
    }
    if (value instanceof File) {
        formData.append(key, value);
        return;
    }
    if (typeof value === 'boolean') {
        formData.append(key, value ? '1' : '0');
        return;
    }
    if (Array.isArray(value)) {
        value.forEach((item, index) => appendToFormData(formData, `${key}[${index}]`, item));
        return;
    }
    if (typeof value === 'object') {
        Object.entries(value).forEach(([nestedKey, nestedValue]) => {
            appendToFormData(formData, `${key}[${nestedKey}]`, nestedValue);
        });
        return;
    }
    formData.append(key, String(value));
};

const submit = () => {
    const payload = { ...form.data() };

    visibilityFields.forEach((key) => {
        payload[key] = form[key] ? 1 : 0;
    });

    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => appendToFormData(formData, key, value));

    for (const [clientKey, file] of Object.entries(seasonPhotoFiles.value)) {
        if (file instanceof File) {
            formData.append(`season_photos[${clientKey}]`, file, file.name);
        }
    }

    for (const [clientKey, marked] of Object.entries(seasonPhotoRemovals.value)) {
        if (marked) {
            formData.append(`season_photo_removals[${clientKey}]`, '1');
        }
    }

    router.post('/profile', formData, {
        preserveScroll: true,
        forceFormData: true,
        onStart: () => { form.processing = true; },
        onFinish: () => { form.processing = false; },
        onError: (errors) => { form.clearErrors(); form.setError(errors); },
    });
};
</script>